import classNames from 'classnames';
import { useAtomValue } from 'jotai';
import { Suspense, useMemo } from 'react';
import { Caption } from '../../../components';
import { LabelBody, LabelBreadcrumbsActive } from '../../modules/components';
import { thermometerDataPointsAtom } from '../../thermometer/data';
import { ThermometerDataPoint } from '../../modules/interfaces';

const maxY = 5;
const minY = 0;
const height = 300;
const verticalPadding = 40;
const horizontalPadding = 20;
const horizontalSpacing = 60;

type Props = {
    className?: string;
};
export function ThermometerGraph(props: Props) {
    return (
        <Suspense fallback={<GraphIsLoading {...props} />}>
            <LazyLoadingGraph {...props} />
        </Suspense>
    );
}

function GraphIsLoading({ className }: Props) {
    return (
        <div className={classNames(className, 'relative')}>
            <LabeledGraph data={[]} className="opacity-60" />
            <LabelBody className="absolute left-0 right-0 top-1/2 text-center">
                Henter data...
            </LabelBody>
        </div>
    );
}

function LazyLoadingGraph(props: Props) {
    const dataPoints = useAtomValue(thermometerDataPointsAtom);
    return <LabeledGraph {...props} data={dataPoints} />;
}

type ThermometerGraphProps = {
    data: ThermometerDataPoint[];
    className?: string;
};

function LabeledGraph({ className, ...props }: ThermometerGraphProps) {
    return (
        <div className={classNames(className, 'flex flex-col')}>
            <div className="flex justify-between">
                <Caption bold>Dine tjek-ind</Caption>
                <LabelBreadcrumbsActive color="text-vibrant-blue" bold>
                    Hvor presset føler du dig?
                </LabelBreadcrumbsActive>
            </div>
            <LabelBreadcrumbsActive
                color="text-grey-textfield-placeholder"
                className="self-end"
            >
                1 = mindst presset og 5 = mest presset
            </LabelBreadcrumbsActive>
            <Graph {...props} />
            <Caption className="self-end" bold>
                Dato
            </Caption>
        </div>
    );
}

function Graph({ data: dataPoints, className }: ThermometerGraphProps) {
    const dates = useMemo(
        () =>
            dataPoints.map((x) => {
                const date = new Date(x.CreatedOn);
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                return `${day}/${month}`;
            }),
        [dataPoints],
    );
    const data = useMemo(
        () => dataPoints.map((x) => x.PressureLevel),
        [dataPoints],
    );
    const width =
        horizontalPadding * 2 + horizontalSpacing * (dates.length - 1);

    const points = data.map((val, i) => {
        const x =
            data.length === 1
                ? width / 2
                : horizontalPadding + i * horizontalSpacing;

        const y =
            verticalPadding +
            ((maxY - val) / (maxY - minY)) * (height - 2 * verticalPadding);
        return { x, y };
    });

    function getSmoothPath(pts: { x: number; y: number }[]) {
        if (pts.length < 2) return '';
        let d = `M ${pts[0].x} ${pts[0].y}`;
        for (let i = 0; i < pts.length - 1; i++) {
            const curr = pts[i];
            const next = pts[i + 1];
            const ctrlX = (curr.x + next.x) / 2;
            d += ` C ${ctrlX} ${curr.y}, ${ctrlX} ${next.y}, ${next.x} ${next.y}`;
        }
        return d;
    }

    const linePath = getSmoothPath(points);
    const fillPath =
        linePath +
        ` L ${points[points.length - 1]?.x ?? 0} ${height - verticalPadding} L ${points[0]?.x ?? 0} ${height - verticalPadding} Z`;

    return (
        <div
            className={classNames(className, 'relative flex h-[300px] w-full')}
        >
            {/* Y-Axis (Sticky) */}
            <div className="relative z-10 w-[20px] shrink-0">
                {[0, 1, 2, 3, 4, 5].map((y) => {
                    const yPos =
                        verticalPadding +
                        ((maxY - y) / (maxY - minY)) *
                            (height - 2 * verticalPadding);
                    return y === 0 ? null : (
                        <LabelBody
                            key={y}
                            className="absolute left-0 pr-2 text-right"
                            style={{ top: yPos - 8 }}
                        >
                            {y}
                        </LabelBody>
                    );
                })}
            </div>

            {/* Scrollable Graph Area */}
            <div className="relative grow touch-pan-x touch-pan-y overflow-x-auto overflow-y-visible">
                <div
                    style={{ minWidth: `${width}px`, height: `${height}px` }}
                    className="relative"
                >
                    <svg
                        width={dataPoints.length ? width : '100%'}
                        height={height}
                        className="shadow absolute left-0 top-0 bg-white"
                    >
                        <defs>
                            <linearGradient
                                id="blueGradient"
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                            >
                                <stop
                                    offset="0%"
                                    stopColor="rgb(0, 71, 255)"
                                    stopOpacity="0.4"
                                />
                                <stop
                                    offset="100%"
                                    stopColor="rgb(0, 71, 255)"
                                    stopOpacity="0"
                                />
                            </linearGradient>
                        </defs>

                        {[0, 1, 2, 3, 4, 5].map((y) => {
                            const yPos =
                                verticalPadding +
                                ((maxY - y) / (maxY - minY)) *
                                    (height - 2 * verticalPadding);
                            return (
                                <line
                                    key={`grid-${y}`}
                                    x1={0}
                                    y1={yPos}
                                    x2="100%"
                                    y2={yPos}
                                    stroke="#e6e6e6"
                                    strokeWidth={1}
                                    strokeDasharray="4 4"
                                />
                            );
                        })}

                        {/* Area fill */}
                        <path d={fillPath} fill="url(#blueGradient)" />

                        {/* Curved line */}
                        <path
                            d={linePath}
                            fill="none"
                            stroke="rgb(0, 71, 255)"
                            strokeWidth="3"
                        />

                        {/* Points */}
                        {points.map(({ x, y }, i) => (
                            <circle
                                key={i}
                                cx={x}
                                cy={y}
                                r={3}
                                fill="white"
                                stroke="rgb(0, 71, 255)"
                                strokeWidth={3}
                            />
                        ))}
                    </svg>

                    {/* X-axis Labels */}
                    {dates.map((label, i) => {
                        const x =
                            data.length === 1
                                ? width / 2
                                : horizontalPadding + i * horizontalSpacing;
                        return (
                            <LabelBody
                                key={`${label}-${i}`}
                                className="absolute"
                                style={{
                                    top: height - verticalPadding + 6,
                                    left: x - 14,
                                }}
                            >
                                {label}
                            </LabelBody>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
